import { Device } from '@twilio/voice-sdk';
import { get_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import {
  attachRemoteAudio,
  getSharedContextState,
  unlockTwilioAudio
} from '../../twilio_agent/twilio_audio';
import {
  DEVICE_OPTIONS,
  attachMediaDiagnostics,
  buildClientSnapshot,
  discardPendingDiag,
  reportCallDiag,
  verifyMicAfterAccept
} from '../../twilio_agent/call_diagnostics';
import { reportDeviceEvent } from '../../twilio_agent/device_diagnostics';

const getUrl = (endpoint) => {
  if (import.meta.env.VITE_TWILIO_CALL_BASE_URL) {
    return `${import.meta.env.VITE_TWILIO_CALL_BASE_URL}${endpoint}`;
  }

  return get_api_url(endpoint);
};

// Per-call diag is buffered until a call_uuid exists, so a dial that dies
// before Twilio ever creates a call leaves no trace at all. These go through
// the device-event channel instead, which needs no call_uuid.
const maskNumber = (value) => {
  const digits = String(value || '');
  return digits.length > 4 ? `****${digits.slice(-4)}` : '****';
};

const errorText = (err) => err?.message || err?.description || String(err || 'unknown error');

// What the agent sees. The SDK's message and code are the only thing that
// distinguishes "no microphone" from "token expired" from "number blocked".
const dialFailureMessage = (err) => {
  const code = err?.code;
  const text = errorText(err);
  return code ? `Failed to place call: ${text} (Twilio ${code})` : `Failed to place call: ${text}`;
};

const reportOutboundFailure = (err, stage, toNumber) => {
  reportDeviceEvent('device_error', {
    reason: stage === 'token' ? 'outbound_token_failed' : 'outbound_connect_failed',
    code: String(err?.code ?? ''),
    data: {
      message: errorText(err),
      stage,
      to_masked: maskNumber(toNumber)
    }
  });
};

const getToken = async () => {
  const url = getUrl('/v2/twilio_call/token');
  const resp = await get_api(url, true);
  return resp.data?.data?.token;
};

export const resolveContact = async (studentUuid) => {
  const url = `${getUrl('/v2/twilio_call/resolve_contact')}?student_uuid=${encodeURIComponent(studentUuid)}`;
  const resp = await get_api(url, true);
  return resp.data?.data;
};

export const getOutgoingCallerId = async (studentUuid) => {
  const url = `${getUrl('/v2/twilio_call/outgoing_caller_id')}?student_uuid=${encodeURIComponent(studentUuid)}`;
  const resp = await get_api(url, true);
  return resp.data?.data?.from_number;
};

// prepareOutbound resolves a typed phone number for the dialer-pad flow.
// Returns { student_uuid, name, from_number, to_number }; student_uuid/name
// are empty strings when no student matches phone_personal.
export const prepareOutbound = async (toNumber) => {
  const url = `${getUrl('/v2/twilio_call/prepare_outbound')}?to=${encodeURIComponent(toNumber)}`;
  const resp = await get_api(url, true);
  return resp.data?.data;
};

export const listCalls = async (studentUuid) => {
  const url = `${getUrl('/v2/twilio_call/calls')}?student_uuid=${encodeURIComponent(studentUuid)}`;
  const resp = await get_api(url, true);
  return resp.data?.data?.calls || [];
};

export const getRecordingURL = async (callUuid) => {
  const url = `${getUrl('/v2/twilio_call/recording_url')}?call_uuid=${encodeURIComponent(callUuid)}`;
  const resp = await get_api(url, true);
  return resp.data?.data?.url;
};

/**
 * Start an outbound call via Twilio Voice SDK.
 * onStatus is invoked with one of: connecting | ringing | connected |
 * reconnecting | failed | ended.
 * The call_uuid is only known once the `outbound_call_started` relay
 * lands, so `getCallUuid` is read lazily; diag events raised before then are
 * buffered by call_diagnostics and flushed by the caller.
 * Returns an object with { hangup, getStatus, teardown, toggleMute } the
 * caller uses to drive and reconcile the call.
 */
export const startCall = async ({
  toNumber,
  fromNumber,
  studentUuid,
  onStatus,
  onMuteChange,
  onAudioWarning,
  getCallUuid
}) => {
  onStatus('connecting');

  let token;
  try {
    token = await getToken();
  } catch (err) {
    reportOutboundFailure(err, 'token', toNumber);
    throw err;
  }
  if (!token) {
    const err = new Error('Failed to fetch Twilio token');
    reportOutboundFailure(err, 'token', toNumber);
    throw err;
  }

  const device = new Device(token, DEVICE_OPTIONS);
  // The dial click's activation window is already gone by here (token +
  // contact lookups awaited above), so this only succeeds on a context some
  // gesture has unlocked — GlobalOutboundCallManager does that on the click.
  unlockTwilioAudio();

  const params = { To: toNumber };
  if (fromNumber) params.CallerId = fromNumber;
  if (studentUuid) params.StudentUUID = studentUuid;

  let call;
  try {
    // Catches both the rejected promise and a synchronous throw. Either way
    // no call_uuid and no CallSid exist yet, so this is the only place the
    // failure can be recorded or shown.
    call = await device.connect({ params });
  } catch (err) {
    reportOutboundFailure(err, 'connect', toNumber);
    try {
      device.destroy();
    } catch (e) {
      /* noop */
    }
    const message = dialFailureMessage(err);
    // After onStatus: the caller clears the warning banner on every terminal
    // status, so warning first would erase it.
    onStatus('failed');
    if (onAudioWarning) onAudioWarning(message);
    const failure = new Error(message);
    failure.code = err?.code;
    throw failure;
  }

  const uuidSource = getCallUuid || null;

  const detachRemoteAudio = attachRemoteAudio(call, {
    onPlayFailed: (err) =>
      reportCallDiag(uuidSource, 'error', {
        callSid: call?.parameters?.CallSid || null,
        data: {
          name: 'remote-audio-play-failed',
          message: err?.message || String(err),
          audio_context_state: getSharedContextState()
        }
      })
  });

  let detachDiagnostics = () => {};
  let cancelMicCheck = () => {};
  const stopDiagnostics = () => {
    detachDiagnostics();
    detachDiagnostics = () => {};
    cancelMicCheck();
    cancelMicCheck = () => {};
    if (onAudioWarning) onAudioWarning(null);
  };

  // Everything before 'accept' predates the call_uuid, so an error there has
  // to be reported as a device event rather than per-call diag.
  let accepted = false;

  call.on('ringing', () => onStatus('ringing'));
  call.on('accept', () => {
    accepted = true;
    onStatus('connected');
    const callSid = call?.parameters?.CallSid || null;
    buildClientSnapshot(device, call)
      .then((snapshot) => reportCallDiag(uuidSource, 'connect', { callSid, snapshot }))
      .catch(() => reportCallDiag(uuidSource, 'connect', { callSid }));
    detachDiagnostics = attachMediaDiagnostics(call, {
      callUuid: uuidSource,
      device,
      onStatus,
      onUserWarning: (message) => onAudioWarning?.(message)
    });
    cancelMicCheck = verifyMicAfterAccept(call, uuidSource, (message) => onAudioWarning?.(message));
  });
  call.on('disconnect', () => {
    onStatus('ended');
    detachRemoteAudio();
    stopDiagnostics();
    device.destroy();
  });
  call.on('cancel', () => {
    onStatus('ended');
    detachRemoteAudio();
    stopDiagnostics();
    discardPendingDiag();
    device.destroy();
  });
  call.on('error', (err) => {
    console.error('Twilio call error', err);
    if (!accepted) reportOutboundFailure(err, 'pre_accept', toNumber);
    onStatus('failed');
    detachRemoteAudio();
    stopDiagnostics();
    device.destroy();
    // Last: stopDiagnostics() and the terminal status both clear the warning.
    if (!accepted && onAudioWarning) onAudioWarning(dialFailureMessage(err));
  });
  call.on('mute', (isMuted) => {
    if (onMuteChange) onMuteChange(!!isMuted);
  });

  return {
    hangup: () => {
      try {
        call.disconnect();
      } catch (e) {
        console.error('hangup error', e);
      }
    },
    // The SDK's own view of the call. Surfaced so callers can reconcile their
    // state against it: the terminal handlers above are not guaranteed to
    // fire (a dead socket, a slept machine, or a discarded tab can swallow
    // 'disconnect'/'cancel'/'error' entirely). Returns a Call.State string —
    // 'closed' means the call is over — or null if the SDK can't tell us.
    getStatus: () => {
      try {
        return call.status();
      } catch (e) {
        return null;
      }
    },
    // Force-release the call and its Device. The terminal handlers above
    // normally destroy the Device; callers need this for the path where they
    // never fired, otherwise the Device stays registered and keeps receiving
    // invites for a call the UI has already given up on.
    teardown: () => {
      try {
        call.disconnect();
      } catch (e) {
        /* noop — caller already logged why it's forcing teardown */
      }
      detachRemoteAudio();
      stopDiagnostics();
      discardPendingDiag();
      try {
        device.destroy();
      } catch (e) {
        /* noop */
      }
    },
    toggleMute: () => {
      try {
        const next = !call.isMuted();
        call.mute(next);
        return next;
      } catch (e) {
        console.error('mute error', e);
        return false;
      }
    }
  };
};
